// ATTICUS-ANALYTICS-COMMAND-CENTER-1 — Accounting / Controller lens.
//
// Payment coding status, payment exceptions, bills by stage, AR aging
// (invoice-financials gated), QBO sync status (read-live/write-off), trust
// in/out + net, and a deferred reserve-adequacy scatter (data not yet reliable).
import React, { useEffect, useState, useMemo } from "react";
import { apiFetch, fmt } from "../../api";
import { KpiCard, ChartCard, buildQS } from "../AnalyticsKit";
import { Donut, HBar, TONE_HEX } from "../charts";
import { toneForStatus } from "../../components/ui";

export default function AccountingLens({ filters, openDrill, reloadKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Accounting metrics are point-in-time; only the claim filters apply (no date window).
  const fkey = JSON.stringify({ bl: filters.business_line, ct: filters.claim_type, st: filters.status,
                                mt: filters.matter_type, ad: filters.adjuster_id, insured: filters.insured_id });
  useEffect(() => {
    let live = true;
    setLoading(true); setError(null);
    const f = { business_line: filters.business_line, claim_type: filters.claim_type, status: filters.status,
                matter_type: filters.matter_type, adjuster_id: filters.adjuster_id, insured_id: filters.insured_id };
    apiFetch(`/analytics/accounting?${buildQS(f)}`)
      .then((d) => { if (live) { setData(d); setLoading(false); } })
      .catch((e) => { if (live) { setError(e.message || String(e)); setLoading(false); } });
    return () => { live = false; };
  }, [fkey, reloadKey]); // eslint-disable-line

  const m = (v) => fmt.money(v);
  const coding = useMemo(() => (data?.payment_coding || [])
    .map((r) => ({ name: r.status, value: r.count, color: TONE_HEX[toneForStatus(r.status)] })), [data]);
  const bills = useMemo(() => (data?.bill_stages || []).map((r) => ({ name: r.stage, value: r.count })), [data]);
  const ar = data?.ar_aging;
  const arData = useMemo(() => (ar?.buckets || []).map((b) => ({ name: b.bucket, value: b.amount, count: b.count })), [ar]);
  const ex = data?.payment_exceptions || {};
  const qbo = data?.qbo;
  const trust = data?.trust;

  if (error) return <div className="an-error">Couldn't load accounting analytics: {error}</div>;
  const L = loading && !data;

  return (
    <div className="an-stack">
      <div className="an-kpis" style={{ gridTemplateColumns: "repeat(4,1fr)" }}>
        <KpiCard label="Uncoded Payments" tone={ex.uncoded_count > 0 ? "amber" : "green"}
                 value={L ? "…" : num(ex.uncoded_count)} sub="need coding"
                 onClick={ex.uncoded_count ? () => openDrill("uncoded_payments", null, "Claims with uncoded payments") : undefined} />
        <KpiCard label="Flagged Duplicates" tone={ex.duplicate_count > 0 ? "red" : undefined}
                 value={L ? "…" : num(ex.duplicate_count)} sub={L ? "" : m(ex.duplicate_amount)} />
        <KpiCard label="AR Outstanding" tone="blue"
                 value={L ? "…" : (ar?.available ? m(ar.total) : "—")}
                 sub={ar?.available ? "sent invoices" : "no access"} />
        <KpiCard label="Trust Net" tone="navy" value={L ? "…" : (trust ? m(trust.net) : "—")}
                 sub={trust ? `${num(trust.in_count)} in / ${num(trust.out_count)} out` : ""} />
      </div>

      <div className="an-grid">
        <ChartCard title="Payment Coding Status" subtitle="coded / uncoded / imported" loading={L} empty={!coding.length}>
          <Donut data={coding} />
        </ChartCard>

        <ChartCard title="Bills by Workflow Stage" subtitle="click a stage to drill" loading={L} empty={!bills.length}>
          <HBar data={bills} onSegmentClick={(p) => openDrill("bill_stage", p.name, `Bills: ${p.name}`)} color="#f59e0b" />
        </ChartCard>

        <ChartCard title="AR Aging" subtitle="sent fee invoices by age" wide loading={L}
                   unavailable={!L && ar && !ar.available ? { status: "Restricted", reason: "AR analytics require invoice-financials access.", dependency: "VIEW_INVOICE_FINANCIALS" } : undefined}
                   empty={ar?.available && !arData.some((d) => d.value > 0)}>
          {ar?.available && <HBar data={arData} money color="#3b6fd4" />}
        </ChartCard>

        <ChartCard title="QBO Sync Status"
                   controls={qbo && <span className="pill pill-blue pill-sm">{qbo.mode}</span>}
                   loading={L}>
          {qbo && (
            <div style={{ padding: "6px 4px" }}>
              <table className="an-exposure-table">
                <thead><tr><th>Realm</th><th>Type</th><th>Status</th><th className="r">Txns</th></tr></thead>
                <tbody>
                  {(qbo.last_runs || []).map((r, i) => (
                    <tr key={i}>
                      <td>{r.company}</td><td>{r.run_type}</td>
                      <td><span className={`pill pill-sm pill-${toneForStatus(r.status)}`}>{r.status}</span></td>
                      <td className="r num">{num(r.transaction_count)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 8 }}>{num(qbo.staging_count)} transactions staged</div>
            </div>
          )}
        </ChartCard>

        <ChartCard title="Trust Activity" subtitle="contributions vs disbursements" wide loading={L}
                   controls={<span className="pill pill-amber pill-sm" title={trust?.variance_reason}>Variance pending</span>}>
          {trust && (
            <HBar data={[{ name: "Contributions (in)", value: trust.inflow, color: TONE_HEX.green },
                         { name: "Disbursements (out)", value: trust.outflow, color: TONE_HEX.red }]} money />
          )}
        </ChartCard>

        <ChartCard title="Reserve Adequacy (Initial vs Ultimate)"
                   unavailable={{ status: "Deferred", reason: data?.deferred?.reserve_adequacy, dependency: "clean reserve history (RP-LOSSRUN-ASOF)" }} />
      </div>
    </div>
  );
}

function num(n) { return n == null ? "—" : Number(n).toLocaleString(); }
